import { Delete } from '@mui/icons-material';
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  TextField,
} from '@mui/material';
import { AutocompleteRenderOptionState } from '@mui/material/Autocomplete/Autocomplete';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import { DatePicker } from '@mui/x-date-pickers-pro';
import moment from 'moment';
import * as React from 'react';
import { ReactNode, useMemo } from 'react';
import { Controller, FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { EmployeeItemInfoData } from '~/components/GenericList/EmployeeItemInfo';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { CostCenter } from '~/data/costCenters';
import { Employees } from '~/data/employees/types';
import {
  BusinessUnit,
  OrgUnitEmployeeRole,
  OrganizationUnit,
  ResponsibleEmps,
} from '~/data/orgunits';
import { OrgUnitType } from '~/scenes/PeopleModule/components/Tabs/BasicTab/data';
import { Office } from '~/scenes/PeopleModule/components/Tabs/ContactTab/components/WorkContact/data';
import { OrgUnitAltManagersFormSection } from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup/components/OrgUnitAltManagersFormSection';
import { OrgUnitFormErrors } from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup/components/OrgUnitFormErrors';
import { OrgUnitSubstituteManagerFormSection } from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup/components/OrgUnitSubstituteManagerFormSection';
import { OrgUnitPopupMode } from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup/types';
import { useOrgUnitPopup } from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup/useOrgUnitPopup';

import { OrgUnitHrResponsibleField } from './components/OrgUnitHrResponsibleField';

export interface OrgUnitPopupProps {
  open: boolean;
  selectedUnit: OrganizationUnit | null;
  handleClose: () => void;
  handleUpdateUnit: (updatedUnit: OrganizationUnit) => void;
  handleDeleteUnit: (deletedUnit: OrganizationUnit) => void;
  handleCreateUnit: (createdUnit: OrganizationUnit) => void;
  unitTypes: OrgUnitType[];
  offices: Office[];
  managerIds: number[];
  teamManagerIds: number[];
  allEmployees: Employees[];
  orgTrees: BusinessUnit[];
  costCenters: CostCenter[];
  hrResponsibleIds: number[];
  alternateMgrIds: number[];
}

export const OrgUnitPopup: React.FC<OrgUnitPopupProps> = (props) => {
  const { open, unitTypes, offices, allEmployees, orgTrees, costCenters } =
    props;
  const { t } = useTranslation();

  const {
    formMethods,
    managers,
    teamManagers,
    hrResponsibles,
    alternateMgrs,
    isFieldDisabled,
    mode: { popupMode },
    permission: { userCanEdit, userCanDelete },
    actions: {
      onSubmit,
      onEditStart,
      onEditCancel,
      onCloseDialog,
      onDeleteUnit,
    },
  } = useOrgUnitPopup(props);

  const { control } = formMethods;

  const textFieldProps: Partial<TextFieldProps> = {
    size: 'small',
    InputLabelProps: { shrink: true },
  };

  const dialogTitle = useMemo(() => {
    const dialogName = t('laOrgUnit');

    switch (popupMode) {
      case OrgUnitPopupMode.New:
        return `${dialogName} - ${t('addNew')}`;
      case OrgUnitPopupMode.Edit:
      case OrgUnitPopupMode.SubstituteEdit:
        return `${dialogName} - ${t('laEdit')}`;
      default:
        return dialogName;
    }
  }, [popupMode, t]);

  const getAutoCompleteProps = <T, K>(props: {
    keyString: string;
    onChange: (value: K) => void;
    defaultValue: K;
    options: T[];
    value: K;
    getKey: (option: T) => K;
    getLabel: (option: T) => string;
  }): Pick<
    AutocompleteProps<T, undefined, undefined, undefined>,
    | 'options'
    | 'getOptionLabel'
    | 'isOptionEqualToValue'
    | 'renderOption'
    | 'value'
    | 'onChange'
  > => {
    const {
      keyString,
      getKey,
      options,
      getLabel,
      defaultValue,
      onChange,
      value,
    } = props;

    return {
      options,
      getOptionLabel: getLabel,
      isOptionEqualToValue: (option: T, value: T) =>
        getKey(option) === getKey(value),
      renderOption: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: T,
        index: AutocompleteRenderOptionState,
      ): ReactNode => {
        return (
          <li {...props} key={`${keyString}-${index}-${getKey(option)}`}>
            {getLabel(option)}
          </li>
        );
      },
      value: options.find((option) => getKey(option) === value) ?? null,
      onChange: (_: unknown, value: T | null) => {
        onChange(!!value ? getKey(value) : defaultValue);
      },
    };
  };

  const getTextFieldErrorProps = (error: FieldError | undefined) => {
    return {
      error: !!error,
      helperText: error ? error.message : null,
    };
  };

  return (
    <DraggableDialog open={open} onClose={onCloseDialog} maxWidth="sm">
      <SolaForceDialogTitle onClose={onCloseDialog}>
        {dialogTitle}
      </SolaForceDialogTitle>
      <DialogContent dividers={true}>
        <form onSubmit={onSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                control={control}
                name="fOrgUnitName"
                render={({
                  field: { onChange, value, ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    {...field}
                    autoFocus
                    required
                    {...textFieldProps}
                    label={t('fOrgUnitName')}
                    type="text"
                    fullWidth
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    inputRef={ref}
                    disabled={isFieldDisabled()}
                    {...getTextFieldErrorProps(error)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                control={control}
                name="fOrgUnitNumber"
                render={({
                  field: { onChange, value, ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    {...field}
                    autoFocus
                    required
                    {...textFieldProps}
                    label={t('fOrgUnitNumber')}
                    type="text"
                    fullWidth
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    inputRef={ref}
                    disabled={isFieldDisabled()}
                    {...getTextFieldErrorProps(error)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                control={control}
                name="fOrgUnitType"
                render={({
                  field: { onChange, value, ref, ...field },
                  fieldState: { error },
                }) => {
                  const autoCompleteProps = getAutoCompleteProps<
                    OrgUnitType,
                    string
                  >({
                    keyString: 'unit-type',
                    defaultValue: '',
                    value,
                    options: unitTypes,
                    getKey: (unitType) => unitType.code,
                    getLabel: (unitType) => unitType.name,
                    onChange,
                  });

                  return (
                    <FormControl fullWidth>
                      <Autocomplete
                        {...field}
                        {...autoCompleteProps}
                        disabled={isFieldDisabled()}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('fOrgUnitType')}
                            required
                            inputRef={ref}
                            {...textFieldProps}
                            {...getTextFieldErrorProps(error)}
                          />
                        )}
                      />
                    </FormControl>
                  );
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                control={control}
                name="fOrgUnitLoc"
                render={({
                  field: { onChange, value, ref, ...field },
                  fieldState: { error },
                }) => {
                  const autoCompleteProps = getAutoCompleteProps<
                    Office,
                    number
                  >({
                    keyString: 'office',
                    defaultValue: 0,
                    value,
                    options: offices,
                    getKey: (office) => office.fOfficeId,
                    getLabel: (office) => office.fOfficeName,
                    onChange,
                  });

                  return (
                    <FormControl fullWidth>
                      <Autocomplete
                        {...field}
                        {...autoCompleteProps}
                        disabled={isFieldDisabled()}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('laOffice')}
                            required
                            inputRef={ref}
                            {...textFieldProps}
                            {...getTextFieldErrorProps(error)}
                          />
                        )}
                      />
                    </FormControl>
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="fOrgUnitUrl"
                render={({
                  field: { onChange, value, ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    {...field}
                    autoFocus
                    {...textFieldProps}
                    label={t('fOrgUnitUrl')}
                    type="text"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    inputRef={ref}
                    disabled={isFieldDisabled()}
                    {...getTextFieldErrorProps(error)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                control={control}
                name="fOrgUnitCreated"
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error },
                }) => (
                  <DatePicker
                    value={value ? moment(value, 'YYYY-MM-DD') : null}
                    onChange={(date) => {
                      const formattedDate = date
                        ? moment(date).format('YYYY-MM-DD')
                        : '';
                      onChange(formattedDate);
                    }}
                    inputRef={ref}
                    disabled={isFieldDisabled()}
                    slotProps={{
                      textField: {
                        label: t('fOrgUnitCreated'),
                        required: true,
                        fullWidth: true,
                        ...textFieldProps,
                        type: 'text',
                        ...getTextFieldErrorProps(error),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                control={control}
                name="fOrgUnitAvail"
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error },
                }) => (
                  <DatePicker
                    value={value ? moment(value, 'YYYY-MM-DD') : null}
                    onChange={(date) => {
                      const formattedDate = date
                        ? moment(date).format('YYYY-MM-DD')
                        : '';
                      onChange(formattedDate);
                    }}
                    inputRef={ref}
                    disabled={isFieldDisabled()}
                    slotProps={{
                      textField: {
                        label: t('fOrgUnitAvail'),
                        required: true,
                        fullWidth: true,
                        ...textFieldProps,
                        type: 'text',
                        ...getTextFieldErrorProps(error),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                control={control}
                name="fOrgUnitManager"
                render={({
                  field: { onChange, value, ref, ...field },
                  fieldState: { error },
                }) => {
                  const autoCompleteProps = getAutoCompleteProps<
                    EmployeeItemInfoData,
                    number | ''
                  >({
                    keyString: 'office',
                    defaultValue: -1,
                    value,
                    options: managers,
                    getKey: (manager) => manager.employeeId,
                    getLabel: (manager) =>
                      `${manager.employee?.fEmpFirstName} ${manager.employee?.fEmpLastName}, ${manager.employee?.fEmpPersonNumber}`,
                    onChange,
                  });

                  return (
                    <FormControl fullWidth>
                      <Autocomplete
                        {...field}
                        {...autoCompleteProps}
                        disabled={isFieldDisabled()}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('fOrgUnitManager')}
                            inputRef={ref}
                            {...textFieldProps}
                            {...getTextFieldErrorProps(error)}
                          />
                        )}
                      />
                    </FormControl>
                  );
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                control={control}
                name="deputyManagers"
                render={({
                  field: { onChange, value, ref, ...field },
                  fieldState: { error },
                }) => (
                  <FormControl fullWidth>
                    <Autocomplete
                      {...field}
                      multiple
                      options={teamManagers}
                      getOptionLabel={(manager) =>
                        `${manager.employee?.fEmpFirstName} ${manager.employee?.fEmpLastName}, ${manager.employee?.fEmpPersonNumber}`
                      }
                      value={teamManagers.filter(
                        (deputy) =>
                          !!value.find(
                            (emp) => emp.fEmployeeId === deputy.employeeId,
                          ),
                      )}
                      onChange={(_, newValue) => {
                        const newDeputyManagers: ResponsibleEmps[] =
                          newValue.map((deputy) => ({
                            fEmployeeId: deputy.employeeId,
                            fUnitRole: OrgUnitEmployeeRole.DeputyManager,
                          }));
                        onChange(newDeputyManagers);
                      }}
                      disabled={isFieldDisabled()}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('laDeputyManager')}
                          inputRef={ref}
                          {...textFieldProps}
                          {...getTextFieldErrorProps(error)}
                        />
                      )}
                    />
                  </FormControl>
                )}
              />
            </Grid>
            <OrgUnitSubstituteManagerFormSection
              form={formMethods}
              allEmployees={allEmployees}
              disabled={isFieldDisabled([OrgUnitPopupMode.SubstituteEdit])}
              commonTextFieldProps={textFieldProps}
            />
            <OrgUnitHrResponsibleField
              form={formMethods}
              hrResponsibles={hrResponsibles}
              disabled={isFieldDisabled()}
              commonTextFieldProps={textFieldProps}
            />
            <Grid item xs={6}>
              <Controller
                control={control}
                name="fOrgUnitContainingTrees"
                render={({
                  field: { onChange, value, ref, ...field },
                  fieldState: { error },
                }) => (
                  <FormControl fullWidth>
                    <Autocomplete
                      {...field}
                      multiple
                      options={orgTrees}
                      getOptionLabel={(orgTree) => orgTree.fTreeName}
                      value={orgTrees.filter((orgTree) =>
                        value.includes(orgTree.fTreeId),
                      )}
                      onChange={(_, newValue) => {
                        const selectedTreeIds = newValue.map(
                          (tree) => tree.fTreeId,
                        );
                        onChange(selectedTreeIds);
                      }}
                      disabled={isFieldDisabled()}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required={true}
                          label={t('fOrgUnitContainingTrees')}
                          inputRef={ref}
                          {...textFieldProps}
                          {...getTextFieldErrorProps(error)}
                        />
                      )}
                    />
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                control={control}
                name="fOrgUnitCostCenters"
                render={({
                  field: { onChange, value, ref, ...field },
                  fieldState: { error },
                }) => (
                  <FormControl fullWidth>
                    <Autocomplete
                      {...field}
                      multiple
                      options={costCenters}
                      getOptionLabel={(cc) =>
                        `${cc.fCostCenterCode}, ${cc.fCostCenterName}`
                      }
                      value={costCenters.filter((cc) =>
                        value.includes(cc.fCostCenterId),
                      )}
                      onChange={(_, newValues) => {
                        const selectedCostCenters = newValues.map(
                          (cc) => cc.fCostCenterId,
                        );
                        onChange(selectedCostCenters);
                      }}
                      disabled={isFieldDisabled()}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('fOrgUnitCostCenters')}
                          inputRef={ref}
                          {...textFieldProps}
                          {...getTextFieldErrorProps(error)}
                        />
                      )}
                    />
                  </FormControl>
                )}
              />
            </Grid>
            <OrgUnitAltManagersFormSection
              form={formMethods}
              alternateMgrs={alternateMgrs}
              disabled={isFieldDisabled()}
              commonTextFieldProps={textFieldProps}
            />
            <OrgUnitFormErrors form={formMethods} />
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box>
            {userCanDelete && (
              <Button
                onClick={onDeleteUnit}
                color={'error'}
                startIcon={<Delete />}
              >
                {t('laDelete')}
              </Button>
            )}
          </Box>
          <Box>
            {!userCanEdit ||
            [OrgUnitPopupMode.Read, OrgUnitPopupMode.New].includes(
              popupMode,
            ) ? (
              <Button onClick={onCloseDialog}>{t('laClose')}</Button>
            ) : (
              <Button onClick={onEditCancel}>{t('laCancel')}</Button>
            )}
            {userCanEdit &&
              (popupMode !== OrgUnitPopupMode.Read ? (
                <Button onClick={onSubmit} color="primary">
                  {t('laSave')}
                </Button>
              ) : (
                <Button onClick={onEditStart} color="primary">
                  {t('laEdit')}
                </Button>
              ))}
          </Box>
        </Box>
      </DialogActions>
    </DraggableDialog>
  );
};
